/**
 * Score notation (canvas 1a, "Notação de score").
 *
 * The rule the canvas states: **the shape is the primary channel, colour reinforces it.**
 * A double ring means eagle-or-better, a single ring means birdie, no mark means par,
 * a square means bogey, a double square means double-or-worse. Someone who cannot
 * distinguish the greens still reads the round correctly.
 *
 * Bogey is deliberately **neutral**, not amber as on the watch: "um bogey não é um alarme".
 */

import { t, type PlainMessageKey } from '@/lib/i18n';

export type ScoreShape = 'eagle' | 'birdie' | 'par' | 'bogey' | 'double';

export function scoreShape(strokes: number, par: number): ScoreShape {
  const delta = strokes - par;
  if (delta <= -2) return 'eagle';
  if (delta === -1) return 'birdie';
  if (delta === 0) return 'par';
  if (delta === 1) return 'bogey';
  return 'double';
}

const SHAPE_LABEL: Record<ScoreShape, PlainMessageKey> = {
  eagle: 'score.shape.eagle',
  birdie: 'score.shape.birdie',
  par: 'score.shape.par',
  bogey: 'score.shape.bogey',
  double: 'score.shape.double',
};

/** Accessible name for a score mark — the visual shape carries no text. */
export function scoreShapeLabel(shape: ScoreShape): string {
  return t(SHAPE_LABEL[shape]);
}

/**
 * Net Double Bogey — the WHS cap on a single hole for handicap purposes.
 * par + 2 + strokes received on that hole. Shown in the profile explainer.
 */
export function netDoubleBogey(par: number, strokesReceived: number): number {
  return par + 2 + strokesReceived;
}

/**
 * How many strokes a player receives on a hole, given a course handicap and the
 * hole's stroke index. Standard WHS allocation: one stroke per hole in SI order,
 * wrapping for handicaps above the hole count.
 */
export function strokesReceivedOnHole(
  courseHandicap: number,
  strokeIndex: number,
  totalHoles: number,
): number {
  if (courseHandicap <= 0) return 0;
  const full = Math.floor(courseHandicap / totalHoles);
  const remainder = courseHandicap % totalHoles;
  return full + (strokeIndex <= remainder ? 1 : 0);
}
